import { useAnalytics } from 'hooks/useAnalytics';
import { SESSION_KEYS, session } from 'utils/storage';

export type LoginMethod = 'apple' | 'email' | 'google' | 'otp' | 'token';

/**
 * Which token-bearing entry point produced a `method: 'token'` login.
 *
 * `method` stays `'token'` for all of them, so anything keyed on it keeps counting every token
 * login; this splits that bucket rather than replacing it. Three mechanisms share the method
 * today and none of them is distinguishable from the others without this:
 * - `verify_link` — the click-to-verify email link. A single-use token in the URL fragment,
 *   exchanged on the VerifyIdentity page (LMD-2273).
 * - `temporary_token` — a `?token=` magic link, exchanged for a session via `verifyUserToken`.
 * - `access_token` — a deep link that already carries a live `accessToken` in the query, so
 *   there is no exchange at all.
 *
 * Not `source`: GA4 reads event parameters named `source`, `medium` and `campaign` as manual
 * traffic attribution, and a login entry point is not a traffic source. `kind` is the word this
 * codebase already uses for a variant discriminant (see `CheckoutSource`).
 */
export type TokenLoginKind = 'access_token' | 'temporary_token' | 'verify_link';

/**
 * `kind` is required when `method` is `token` and rejected otherwise, so a new token entry point
 * cannot land untagged — the compiler stops it rather than a convention someone has to remember.
 *
 * The alternative, an optional property set only on the newest path, encodes the older ones as
 * *absence*: indistinguishable from a client build that predates the property, and silently wrong
 * the first time anything else sets it.
 */
export type LoginProperties =
  { kind: TokenLoginKind; method: 'token' } | { kind?: never; method: NonTokenLoginMethod };

/** Every method except `token`, the one that has to name a `TokenLoginKind`. */
export type NonTokenLoginMethod = Exclude<LoginMethod, 'token'>;

export type CheckoutProperties = {
  currency: string;
  indication?: string;
  period?: string;
  revenue: number;
};

const isInWebView = () => session.get(SESSION_KEYS.MOBILE_WEBVIEW) === 'true';

const callBridge = (
  method: string,
  properties: Record<string, unknown>,
  eventName?: string
): void => {
  if (!window?.flutter_inappwebview) return;
  window.flutter_inappwebview.callHandler('analytics', {
    ...(eventName !== undefined && { eventName }),
    method,
    properties
  });
};

export const useAnalyticsManager = () => {
  const logFirebaseEvent = useAnalytics();

  const trackLead = ({ email }: { email: string }) => {
    if (isInWebView()) {
      callBridge('generate_lead', { email });
    } else {
      logFirebaseEvent('lead', { email });
    }
  };

  const trackLogin = ({ kind, method }: LoginProperties) => {
    // Omitted rather than sent as `undefined` when there is no kind, same as `indication` and
    // `period` on the checkout events — and on both sinks, so the WebView shell and the web see
    // the same event.
    const properties: Record<string, unknown> = { method };
    if (kind !== undefined) properties.kind = kind;

    if (isInWebView()) {
      callBridge('login', properties);
    } else {
      logFirebaseEvent('login', properties);
    }
  };

  const trackSignUp = () => {
    if (isInWebView()) {
      callBridge('sign_up', {});
    } else {
      logFirebaseEvent('sign_up');
    }
  };

  const trackBeginCheckout = ({ currency, indication, period, revenue }: CheckoutProperties) => {
    if (isInWebView()) {
      // WebView shell: bridge contract keeps the high-level `revenue` field — the mobile-side
      // handler owns the per-tracker mapping (Firebase value, AppsFlyer af_revenue).
      const properties: Record<string, unknown> = { currency, revenue };
      if (indication !== undefined) properties.indication = indication;
      if (period !== undefined) properties.period = period;
      callBridge('begin_checkout', properties);
    } else {
      // Firebase web SDK: the `begin_checkout` reserved event uses `value`, not `revenue`
      // (GA4 spec). Map the high-level `revenue` field to `value` so GA4 ecommerce reports
      // pick up the amount correctly.
      const properties: Record<string, unknown> = { currency, value: revenue };
      if (indication !== undefined) properties.indication = indication;
      if (period !== undefined) properties.period = period;
      logFirebaseEvent('begin_checkout', properties);
    }
  };

  const trackPurchase = ({ currency, indication, period, revenue }: CheckoutProperties) => {
    if (isInWebView()) {
      // WebView shell: bridge contract keeps the high-level `revenue` field — the mobile-side
      // handler owns the per-tracker mapping (Firebase value, AppsFlyer af_revenue).
      const properties: Record<string, unknown> = { currency, revenue };
      if (indication !== undefined) properties.indication = indication;
      if (period !== undefined) properties.period = period;
      callBridge('purchase', properties);
    } else {
      // Firebase web SDK: the `purchase` reserved event uses `value`, not `revenue` (GA4 spec).
      // Map the high-level `revenue` field to `value` so GA4 ecommerce reports pick up the
      // amount correctly.
      const properties: Record<string, unknown> = { currency, value: revenue };
      if (indication !== undefined) properties.indication = indication;
      if (period !== undefined) properties.period = period;
      logFirebaseEvent('purchase', properties);
    }
  };

  const track = (eventName: string, properties: Record<string, unknown> = {}) => {
    if (isInWebView()) {
      callBridge('track', properties, eventName);
    } else {
      logFirebaseEvent(eventName, properties);
    }
  };

  return {
    track,
    trackBeginCheckout,
    trackLead,
    trackLogin,
    trackPurchase,
    trackSignUp
  };
};
